'use client';

// Recovered from the IMAI v5 animation source; demo documents stay illustrative.
import * as React from 'react';
import { ShowcaseCopyContext, translateShowcaseNode } from '../translation';
import { bs, vs } from './playback-controls';
import { referenceProducts, fe } from './reference-products';
import { ProductRender } from './scene-primitives';
export const xs = (e) =>
  bs.find((t) => t.name === e) ?? {
    name: e,
    hex: '#8B7E6E',
  };
export const Ss = [
  {
    key: 'doubleface',
    name: 'Double-face wool',
    short: 'Double-face',
    weave: 'twill',
  },
  {
    key: 'flannel',
    name: 'Wool flannel',
    short: 'Flannel',
    weave: 'diag',
  },
  {
    key: 'worsted',
    name: 'Matte worsted',
    short: 'Worsted',
    weave: 'diag',
  },
  {
    key: 'woolcash',
    name: 'Wool-cashmere',
    short: 'Wool-cash.',
    weave: 'crepe',
  },
  {
    key: 'brushed',
    name: 'Brushed wool',
    short: 'Brushed',
    weave: 'rib',
  },
];
export const Cs = [
  {
    options: 2,
    drop: 'Sep',
    material: 'doubleface',
  },
  {
    options: 3,
    drop: 'Aug',
    material: 'woolcash',
  },
  {
    options: 2,
    drop: 'Aug',
    material: 'flannel',
  },
  {
    options: 2,
    drop: 'Sep',
    material: 'woolcash',
  },
  {
    options: 2,
    drop: 'Oct',
    material: 'brushed',
  },
  {
    options: 1,
    drop: 'Sep',
    material: 'worsted',
  },
];
export const ws = {
  options: 1,
  drop: 'Sep',
  material: 'flannel',
};
export const V = referenceProducts.map((e, t) => ({
  style: e.style,
  name: e.name,
  category: e.category,
  role: e.role,
  price: e.price,
  colour: xs(e.colorway),
  image: vs[t],
  ...(Cs[t] ?? ws),
}));
export const Ts = (e) => `$${e.toLocaleString('en-US')}`;
export function Es(e, { y: t = 6, scale: n = 1, blur: r = 0 } = {}) {
  return {
    opacity: +!!e,
    transform: e ? 'none' : `translateY(${t}px) scale(${n})`,
    filter: e || !r ? 'none' : `blur(${r}px)`,
  };
}
export const Os =
  'transition-[opacity,transform,filter] duration-[480ms] ease-[var(--ease)]';
export function Scene_ks({ children: e, className: t = '' }) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  return (
    <p className={`label ${t}`}>{translateShowcaseNode(showcaseCopy, e)}</p>
  );
}
export function CollectionReveal({
  on: e,
  children: t,
  className: n = '',
  y: r,
  scale: i,
  blur: a,
  as: Component_o = 'div',
  style: s,
}) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  return (
    <Component_o
      className={`${Os} ${n}`}
      style={{
        ...Es(e, {
          y: r,
          scale: i,
          blur: a,
        }),
        ...s,
      }}
    >
      {translateShowcaseNode(showcaseCopy, t)}
    </Component_o>
  );
}
export function Scene_As({ hex: e, className: t = 'size-2.5' }) {
  return (
    <span
      aria-hidden={!0}
      className={`inline-block shrink-0 rounded-full shadow-[inset_0_0_0_1px_color-mix(in_oklch,var(--foreground)_22%,transparent)] ${t}`}
      style={{
        backgroundColor: e,
      }}
    />
  );
}
export function Scene_js({ piece: e, className: t }) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  return e.image ? (
    <ProductRender
      render={e.image}
      alt={translateShowcaseNode(showcaseCopy, e.image.alt)}
      className={t}
    />
  ) : (
    <span className={`bg-[var(--tint)] ${t}`} />
  );
}
export function Scene_Ms({
  d: e,
  on: t,
  ms: n = 700,
  delay: r = 0,
  className: i = '',
  strokeWidth: a = 1.5,
}) {
  return (
    <path
      d={e}
      pathLength={1}
      fill={'none'}
      stroke={'currentColor'}
      strokeWidth={a}
      strokeLinecap={'round'}
      strokeLinejoin={'round'}
      strokeDasharray={'1 2'}
      strokeDashoffset={+!t}
      className={i}
      style={{
        transition: t ? `stroke-dashoffset ${n}ms var(--ease) ${r}ms` : 'none',
      }}
    />
  );
}
export function Scene_Ns({ on: e, className: t = 'size-4' }) {
  return (
    <svg viewBox={'0 0 20 20'} aria-hidden={!0} className={t}>
      <Scene_Ms
        d={'m5 10.5 3.2 3.2L15 6.8'}
        on={e}
        ms={360}
        strokeWidth={1.8}
      />
    </svg>
  );
}
export function Ps(e) {
  let t = Number.parseInt(e.replace('#', ''), 16),
    n = (t >> 16) & 255,
    r = (t >> 8) & 255,
    i = t & 255;
  return 0.299 * n + 0.587 * r + 0.114 * i > 150 ? '#1C1A17' : '#F2EEE6';
}
export const Fs = fe;
export const Is = (e) => Fs.leadTimes.find((t) => t.item === e)?.weeks ?? '';
export function Ls(e) {
  let t = Fs.gauges.find((t) => t.piece === e.style);
  if (t) {
    let e = 'fallback' in t ? `, ${t.fallback} fallback` : '';
    return {
      label: 'Gauge',
      value: `${t.gauge}${e}`,
    };
  }
  return {
    label: 'Lead time',
    value: `${e.category === 'Knitwear' ? Is('Sweater knitwear') : Is('Tailored jackets')} weeks`,
  };
}
export const Rs = V.map((e) => {
  let t = Ls(e),
    n = [
      {
        section: 'Silhouette',
        value: 'Soft, precise',
        open: !1,
      },
      {
        section: 'Colour',
        value: e.colour.name,
        open: !1,
      },
      {
        section: t.label,
        value: t.value,
        open: !1,
      },
      {
        section: 'Fibre composition',
        value: 'Final composition open',
        open: !0,
      },
      {
        section: 'Mill and origin',
        value: 'Mill, origin, MOQ, cost open',
        open: !0,
      },
    ],
    r = n.filter((e) => !e.open).length;
  return {
    piece: e,
    spec: t.value,
    rows: n,
    ready: r / n.length,
  };
});
export const zs = Fs.unknowns;
export const Bs = Fs.gates;
export const Vs = (e) => 300 + e * 620;
export const Hs = (e, t) => Vs(e) + 200 + t * 80;
export const Us = (e) => Vs(e) + 480;
export const Ws = Vs(Rs.length - 1) + 700;
export const Gs = Ws + Bs.length * 110 + 200;
export const Ks = Gs + 600;
export const qs = fe.leadTimes
  .map((e) => `${e.item.toLowerCase()} ${e.weeks} wk`)
  .join(' \xB7 ');
export const Js = 110;
